import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskLine, EASE } from "./motion";
import { TeamSearch } from "./TeamSearch";

const HEX = "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)";

const HexPhoto = ({ rx, ry, imgY }) => (
  <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 1100 }} className="relative mx-auto aspect-[0.866] w-[78%] max-w-[520px] sm:w-[60%] lg:w-full">
    <svg viewBox="0 0 86.6 100" className="absolute -inset-[5%] h-[110%] w-[110%] text-[#39FF14]/50" fill="none" stroke="currentColor" strokeWidth="0.4">
      <motion.polygon points="43.3,0 86.6,25 86.6,75 43.3,100 0,75 0,25" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.2, ease: EASE, delay: 0.6 }} />
    </svg>
    <motion.div
      initial={{ clipPath: "polygon(50% 50%, 50% 50%, 50% 50%, 50% 50%, 50% 50%, 50% 50%)" }}
      animate={{ clipPath: HEX }}
      transition={{ duration: 1.4, ease: EASE, delay: 0.3 }}
      className="absolute inset-0 overflow-hidden bg-[#0F172A]"
    >
      <motion.img style={{ y: imgY, scale: 1.2 }} src="/img/dunk.jpg" alt="Player rising for a dunk" className="h-full w-full object-cover object-[50%_20%] grayscale contrast-125" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_45%_25%,rgba(57,255,20,0.0)_0%,rgba(10,14,26,0.35)_45%,rgba(10,14,26,0.9)_100%)]" />
      <div className="absolute inset-0 bg-[#39FF14]/15 mix-blend-color" />
    </motion.div>
    <motion.div
      style={{ translateZ: 60 }}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1.6, duration: 0.8, ease: EASE }}
      className="absolute -left-4 bottom-[18%] rounded-2xl border border-white/10 bg-[#0A0E1A]/70 px-4 py-3 backdrop-blur-xl sm:-left-10"
    >
      <p className="font-mono-gd text-[10px] uppercase tracking-[0.25em] text-slate-400">Sample · Prospect card</p>
      <p className="font-mono-gd text-xl font-bold text-white">TS% <span className="text-[#39FF14]">61.2</span></p>
    </motion.div>
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1.8, duration: 0.8, ease: EASE }}
      className="absolute -right-2 top-[16%] flex items-center gap-2 rounded-full bg-[#39FF14] px-4 py-2 font-mono-gd text-xs font-bold uppercase tracking-wider text-[#0A0E1A] sm:-right-6"
    >
      <span className="h-2 w-2 animate-pulse rounded-full bg-[#0A0E1A]" />Top Target
    </motion.div>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const ghostX = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <section ref={ref} onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden pb-16 pt-28">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[640px] w-[640px] rounded-full bg-[#39FF14]/10 blur-[140px]" />
      <div className="court-grid pointer-events-none absolute inset-0 opacity-40" />
      <motion.p style={{ x: ghostX }} className="text-outline pointer-events-none absolute -bottom-[2vw] left-0 whitespace-nowrap font-display text-[22vw] font-black uppercase leading-[0.75] text-transparent">
        Courtside
      </motion.p>
      <div className="relative mx-auto grid w-full max-w-[1400px] items-center gap-14 px-5 sm:px-10 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-6 flex items-center gap-3 font-mono-gd text-xs uppercase tracking-[0.3em] text-[#39FF14]">
            <span className="h-px w-10 bg-[#39FF14]" />GAMEDAY Courtside by HoopsHQ · HoopsHQ.net
          </motion.p>
          <h1 className="font-display text-[15vw] font-black uppercase leading-[0.84] tracking-tight text-white sm:text-8xl lg:text-[6.4rem] xl:text-[7.6rem]">
            <MaskLine delay={0.15}>The game</MaskLine>
            <MaskLine delay={0.28}>doesn't end</MaskLine>
            <MaskLine delay={0.41} className="text-[#39FF14]">at the buzzer.</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease: EASE }} className="mt-8 max-w-xl text-base text-slate-300 md:text-lg">
            Your GAMEDAY program on the big screen. Roster analytics, shot zones, combine numbers and recruiter conversations, with the same login you use on your phone. HoopsHQ.net is the home of GAMEDAY Courtside.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 0.8, ease: EASE }} className="relative z-20 mt-8">
            <p className="mb-2 font-mono-gd text-xs uppercase tracking-[0.2em] text-slate-400">Fans: follow your team</p>
            <TeamSearch />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8, ease: EASE }} className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/login" data-testid="hero-login-button" className="group flex items-center gap-3 rounded-full bg-[#39FF14] py-4 pl-7 pr-4 font-bold text-[#0A0E1A] transition-[box-shadow] hover:shadow-[0_0_50px_rgba(57,255,20,0.5)]">
              Log in to Courtside
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0A0E1A] text-[#39FF14] transition-transform duration-300 group-hover:rotate-[-45deg]"><ArrowRight className="h-4 w-4" /></span>
            </Link>
            <Link to="/join" data-testid="hero-join-link" className="rounded-full border border-white/15 px-6 py-4 font-semibold text-white transition-colors hover:border-[#39FF14]/60">Join now</Link>
            <Link to="/invite-code" data-testid="hero-invite-code-link" className="rounded-full border border-white/15 px-6 py-4 font-semibold text-white transition-colors hover:border-[#39FF14]/60">Have an invite code?</Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }} className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
            <span className="font-mono-gd text-xs uppercase tracking-[0.2em] text-slate-400">No account needed:</span>
            <Link to="/demo" data-testid="hero-demo-team-link" className="group inline-flex items-center gap-2 font-semibold text-white underline decoration-[#39FF14]/60 underline-offset-4 hover:text-[#39FF14]">Explore a sample team<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
            <Link to="/demo/players/demo-p1" data-testid="hero-demo-player-link" className="group inline-flex items-center gap-2 font-semibold text-white underline decoration-[#39FF14]/60 underline-offset-4 hover:text-[#39FF14]">View a sample player report<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5">
          <HexPhoto rx={rx} ry={ry} imgY={imgY} />
        </div>
      </div>
    </section>
  );
};
